import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Loader2, Bot, User, Sparkles, TrendingUp, Target, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quick question suggestions
const QUICK_QUESTIONS = [
  { icon: TrendingUp, label: "Is it bullish or bearish?", query: "Based on the current technical and fundamental data, is this stock bullish or bearish right now? What are the key indicators?" },
  { icon: Target, label: "Best options strategy?", query: "What options strategy would you recommend for this stock right now given its current price and volatility?" },
  { icon: AlertCircle, label: "Key risks?", query: "What are the main risks and things I should watch out for with this stock?" },
  { icon: Sparkles, label: "Entry/exit points", query: "What are good entry and exit points for this stock? Include support and resistance levels." },
];

const StockAnalysisChat = ({ stockData, symbol }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `stock_chat_${symbol}_${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize with welcome message when stock data is available
  useEffect(() => {
    if (stockData && messages.length === 0) {
      const initialMessage = {
        role: 'assistant',
        content: `I'm ready to analyze **${symbol}** for you! Current price: **$${stockData.current_price?.toFixed(2)}** (${stockData.change >= 0 ? '+' : ''}${stockData.change_percent?.toFixed(2)}%)

I can help you with:
• Technical analysis & price targets
• Options strategy recommendations
• Risk assessment
• Entry/exit points
• Market sentiment

Ask me anything or use a quick question below!`
      };
      setMessages([initialMessage]);
    }
  }, [stockData, symbol]);

  // Reset chat when symbol changes
  useEffect(() => {
    if (symbol) {
      setMessages([]);
    }
  }, [symbol]);

  // Scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (messageText = null) => {
    const userMessage = messageText || inputValue.trim();
    if (!userMessage || isLoading) return;

    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Use TradeMaster endpoint for stock analysis chat
      const response = await axios.post(`${API}/trademaster/chat`, {
        session_id: sessionId,
        message: userMessage,
        include_market_data: true,
        context: {
          symbol: symbol,
          stock_data: stockData ? {
            current_price: stockData.current_price,
            change: stockData.change,
            change_percent: stockData.change_percent,
            volume: stockData.volume,
            market_cap: stockData.market_cap,
            pe_ratio: stockData.pe_ratio,
            fifty_two_week_high: stockData.fifty_two_week_high,
            fifty_two_week_low: stockData.fifty_two_week_low,
            name: stockData.name
          } : null,
          recent_messages: messages.slice(-4).map(m => ({ role: m.role, content: m.content.substring(0, 200) }))
        }
      });

      if (response.data.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response 
        }]);
      } else {
        throw new Error(response.data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I encountered an error. Please try again." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = (query) => {
    sendMessage(query);
  };

  if (!stockData) return null;

  return (
    <Card className="glass-effect border-0 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg">
          <MessageCircle className="h-5 w-5 mr-2 text-indigo-600" />
          AI Stock Analysis Chat
          <span className="ml-2 flex items-center text-xs bg-green-500/20 text-green-600 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></span>
            LIVE
          </span>
        </CardTitle>
        <CardDescription>Ask questions about {symbol} - powered by real-time market data</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Questions */}
        <div className="flex flex-wrap gap-2">
          {QUICK_QUESTIONS.map((q, idx) => (
            <Button
              key={idx}
              variant="outline"
              size="sm"
              onClick={() => handleQuickQuestion(q.query)}
              disabled={isLoading}
              className="text-xs hover:bg-indigo-50 hover:border-indigo-300"
            >
              <q.icon className="h-3 w-3 mr-1" />
              {q.label}
            </Button>
          ))}
        </div>

        {/* Chat Messages */}
        <ScrollArea className="h-[300px] pr-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    {msg.role === 'user' ? (
                      <User className="h-4 w-4" />
                    ) : (
                      <Bot className="h-4 w-4 text-indigo-600" />
                    )}
                    <span className="text-xs font-medium">
                      {msg.role === 'user' ? 'You' : 'AI Analyst'}
                    </span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">
                    {msg.content.split('**').map((part, i) => 
                      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                    <span className="text-sm text-gray-600">Analyzing...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder={`Ask about ${symbol}...`}
            disabled={isLoading}
            className="flex-1"
            data-testid="stock-chat-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={isLoading || !inputValue.trim()}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
            data-testid="stock-chat-send"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StockAnalysisChat;
